import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getBuilder, getCapsByBuilder, getStripeCustomer, getLatestStoredImages, patchCapDefaultMonitor, getSharedCaps, getCaps } from '../utils/bedrockAPI';
import { getUser, hasOwnerAccess, reparseCaps, getCapTitleLengthClass } from '../utils/Common';
import CancelServiceModal from '../components/CancelServiceModal';
import AddCameraModal from '../components/AddCameraModal';
import TimelapseModal from '../components/TimelapseModal';
import LoadingSpinner from '../components/access-control/LoadingSpinner';
import './CameraManagement.css';

function getCapStatusClass(status) {
  if (status === 'ACTIVE') return 'camera-status-badge--active';
  if (status === 'BUILD') return 'camera-status-badge--build';
  return 'camera-status-badge--inactive';
}

function getCapStatusLabel(status) {
  if (status === 'ACTIVE') return 'Active';
  if (status === 'BUILD') return 'Pending Activation';
  return status || 'Inactive';
}

export default function CameraManagement(props) {
  const builderId = props.match.params.builderid;
  const [builder, setBuilder] = useState(null);
  const [caps, setCaps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTimelapseModal, setShowTimelapseModal] = useState(false);
  const [selectedCap, setSelectedCap] = useState(null);
  const [stripeCustomer, setStripeCustomer] = useState(null);
  const [stripeCustomerLoaded, setStripeCustomerLoaded] = useState(false);
  const [isMounted, setIsMounted] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedCapId, setExpandedCapId] = useState(null);
  const [monitorImages, setMonitorImages] = useState({});
  const [userCaps, setUserCaps] = useState([]);

  const currentUser = getUser();
  const canManageBilling = currentUser.role === 'ADMIN'
    || currentUser.builderStatus === 'BILLING'
    || currentUser.builderStatus === 'ADMIN';
  const isBedrockAdmin = currentUser.role === 'ADMIN';

  useEffect(() => {
    let active = true;
    async function loadBuilder() {
      try {
        const tempBuilder = await getBuilder(builderId);
        if (active) setBuilder(tempBuilder);
      } catch (error) {
        console.error('Error loading builder:', error);
      }
    }
    loadBuilder();
    loadCaps();
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      active = false;
      setIsMounted(false);
      window.removeEventListener('resize', checkMobile);
    };
  }, [builderId]);

  useEffect(() => {
    loadStripeCustomer();
  }, []);

  useEffect(() => {
    async function loadUserCaps() {
      if (currentUser && currentUser.userId) {
        try {
          const capsForUser = await getSharedCaps(currentUser.userId);
          setUserCaps(capsForUser);
        } catch (error) {
          console.error('Error loading user caps:', error);
          setUserCaps([]);
        }
      }
    }
    loadUserCaps();
  }, []);

  const loadCaps = async () => {
    try {
      let allCaps = [];
      if (canManageBilling) {
        allCaps = await getCapsByBuilder(builderId);
      } else {
        const sharedCaps = await getSharedCaps(currentUser.userId);
        const capsForUser = reparseCaps(await getCaps());
        allCaps = capsForUser.filter(cap =>
          sharedCaps.some(userCap => userCap.accessType === 'OWNER' && userCap.capId === cap.capId)
        );
      }
      setCaps(allCaps);
    } catch (error) {
      console.error('Error loading caps:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStripeCustomer = async () => {
    if (stripeCustomerLoaded) return;
    try {
      const customer = await getStripeCustomer(builderId);
      if (isMounted) {
        setStripeCustomer(customer);
        setStripeCustomerLoaded(true);
      }
    } catch (error) {
      if (isMounted) setStripeCustomerLoaded(true);
    }
  };

  useEffect(() => {
    async function loadMonitorImages() {
      if (caps.length === 0) return;
      for (let i = 0; i < caps.length; i++) {
        try {
          const images = await getLatestStoredImages(caps[i].capId);
          if (isMounted) {
            setMonitorImages(prev => ({
              ...prev,
              [caps[i].capId]: images
            }));
          }
        } catch (error) {
          console.error('Error loading monitor images:', error);
        }
      }
    }
    loadMonitorImages();
    if (isMounted && caps.length > 0 && !expandedCapId) {
      setExpandedCapId(caps[0].capId);
    }
  }, [caps]);

  const handleCancelClick = (cap) => {
    setSelectedCap(cap);
    setShowCancelModal(true);
  };

  const handleCapClick = (capId) => {
    setExpandedCapId(expandedCapId === capId ? null : capId);
  };

  const hasAccessToCap = (capId) => {
    if (canManageBilling) return true;
    return userCaps.some(userCap => userCap.capId === capId && userCap.accessType === 'OWNER');
  };

  const handleMonitorSelect = async (capId, monitorId) => {
    try {
      await patchCapDefaultMonitor(capId, monitorId);
      setCaps(prevCaps => prevCaps.map(cap =>
        cap.capId === capId ? { ...cap, defaultMonitorId: monitorId } : cap
      ));
    } catch (error) {
      console.error('Error setting default monitor:', error);
    }
  };

  return (
    <div className="camera-management-page">
      <header className="camera-management-header">
        <nav className="brand-breadcrumb">
          <Link to="/dashboard">Dashboard</Link>
          <span className="brand-breadcrumb-sep">/</span>
          {isBedrockAdmin && (
            <>
              <Link to={`/accesscontrol/${builderId}`}>Access Control</Link>
              <span className="brand-breadcrumb-sep">/</span>
            </>
          )}
          <span className="brand-breadcrumb-current">Camera Management</span>
        </nav>
        <div className="camera-management-title-row">
          {builder && builder.name ? (
            <h1 className={`brand-cap-title ${getCapTitleLengthClass(builder.name)}`}>
              {builder.name}
            </h1>
          ) : (
            <h1 className="camera-management-title">Camera Management</h1>
          )}
          <div className="header-buttons">
            {/* {isBedrockAdmin && (
              <Button
                className="btn-brand-outline btn-compact"
                onClick={() => props.history.push(`/accesscontrol/${builderId}`)}
              >
                <i className="fas fa-arrow-left" /> {isMobile ? 'Access Control' : 'Back to Access Control'}
              </Button>
            )} */}
            {canManageBilling && (
              <Button
                className="btn-brand-dark btn-compact"
                onClick={() => setShowAddModal(true)}
                disabled={!stripeCustomer}
              >
                <i className="fas fa-plus-circle" /> Order Camera
              </Button>
            )}
            <Button
              className="btn-brand-outline btn-compact"
              onClick={() => props.history.push(`/timelapse/${builderId}`)}
            >
              <i className="fas fa-film" /> {isMobile ? 'Timelapses' : 'View Timelapses'}
            </Button>
          </div>
        </div>
        <p className="camera-management-subtitle">
          Order cameras, manage services, and set primary views
        </p>
      </header>

      {stripeCustomerLoaded && !stripeCustomer && canManageBilling && (
        <div className="camera-management-notice">
          <i className="fas fa-info-circle" />
          Contact Bedrock Wireless to add or cancel services as your account receives special pricing.
        </div>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : caps.length === 0 ? (
        <p className="camera-management-empty">No cameras found for this account.</p>
      ) : (
        <section className="camera-management-panel">
          <h2 className="camera-management-section-title">Your Cameras</h2>
          <div className="camera-list">
            {caps.map(cap => {
              const isExpanded = expandedCapId === cap.capId;
              const showMonitorPanel = isExpanded
                && monitorImages[cap.capId]
                && hasAccessToCap(cap.capId)
                && cap.status !== 'BUILD';
              const is2Cam = cap.architecture && cap.architecture.includes('2CAM');
              const is4Cam = cap.architecture && (
                cap.architecture.includes('4CAM') || cap.architecture.includes('MP4 JETSON BROMINE')
              );

              return (
                <article key={cap.capId} className="camera-card">
                  <div
                    className={`camera-card-header ${isExpanded ? 'camera-card-header--expanded' : ''}`}
                    onClick={() => handleCapClick(cap.capId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleCapClick(cap.capId)}
                  >
                    <div className="camera-card-info">
                      <span className={`camera-card-chevron ${isExpanded ? 'camera-card-chevron--open' : ''}`}>
                        <i className="fas fa-chevron-right" />
                      </span>
                      <div>
                        <h3 className="camera-card-name">{cap.name}</h3>
                        <span className={`camera-status-badge ${getCapStatusClass(cap.status)}`}>
                          {getCapStatusLabel(cap.status)}
                        </span>
                      </div>
                    </div>
                    <div className="camera-card-actions" onClick={(e) => e.stopPropagation()}>
                      {cap.status === 'BUILD' ? (
                        <Button
                          className="btn-brand-dark btn-compact"
                          onClick={() => props.history.push(`/signupnewcap/${cap.capId}`)}
                        >
                          Activate
                        </Button>
                      ) : (
                        <>
                          {hasAccessToCap(cap.capId) && (
                            <Button
                              className="btn-brand-outline btn-compact"
                              onClick={() => {
                                setSelectedCap(cap);
                                setShowTimelapseModal(true);
                              }}
                            >
                              Generate Timelapse
                            </Button>
                          )}
                          {canManageBilling && (
                            <Button
                              className="btn-brand-outline btn-compact camera-management-btn-danger"
                              disabled={!stripeCustomer}
                              onClick={() => handleCancelClick(cap)}
                            >
                              Cancel Service
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {showMonitorPanel && (
                    <div className="monitor-selection-panel">
                      <h4 className="monitor-selection-title">Select Your Primary Camera View</h4>
                      <div className={`monitor-grid ${is4Cam ? 'monitor-grid-2x2' : 'monitor-grid-1x4'}`}>
                        {Object.entries(monitorImages[cap.capId])
                          .filter(([, monitorData]) => !is2Cam || monitorData.id <= 2)
                          .map(([monitorId, monitorData]) => (
                            <button
                              key={monitorId}
                              type="button"
                              className={`monitor-option ${cap.defaultMonitorId === monitorId ? 'monitor-option--selected' : ''}`}
                              onClick={() => handleMonitorSelect(cap.capId, monitorId)}
                            >
                              <img src={monitorData.url} alt={`Monitor ${monitorData.id}`} />
                              <div className="monitor-label">
                                Camera {monitorData.id}
                                {cap.defaultMonitorId === monitorId && ' (Default)'}
                              </div>
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}

      {stripeCustomer && (
        <CancelServiceModal
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          cap={selectedCap}
          stripeCustomer={stripeCustomer}
          builderId={builderId}
        />
      )}

      {stripeCustomer && (
        <AddCameraModal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          stripeCustomer={stripeCustomer}
          builderId={builderId}
        />
      )}

      {selectedCap && (
        <TimelapseModal
          show={showTimelapseModal}
          onHide={() => setShowTimelapseModal(false)}
          cap={selectedCap}
          monitorImages={monitorImages[selectedCap.capId]}
        />
      )}
    </div>
  );
}
